import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useLoginMutation,
  useLogoutMutation,
  useInitializeAuthMutation,
} from "./authApi";
import { selectIsInitialized, selectAuthState, clearError } from "./authSlice";
import { extractErrorMessage } from "../../../../utils/errorHandler";

/**
 * Кастомные хуки аутентификации EyeHub (SSO-only).
 */

/**
 * Основной хук управления аутентификацией.
 * Предоставляет login (SSO-redirect) / logout + текущее состояние.
 */
export const useAuth = () => {
  const dispatch = useDispatch();

  const [loginMutation, { isLoading: isLoginLoading }] = useLoginMutation();
  const [logoutMutation, { isLoading: isLogoutLoading }] = useLogoutMutation();

  const authState = useSelector(selectAuthState);
  const isLoading = authState.isLoading || isLoginLoading || isLogoutLoading;

  // SSO: мутация инициирует redirect на Keycloak. Возврат/заполнение user
  // происходит после callback через initializeAuth.
  const login = async () => {
    try {
      await loginMutation().unwrap();
      return { success: true };
    } catch (error) {
      return { success: false, error: extractErrorMessage(error, "Ошибка входа в систему") };
    }
  };

  const logout = async () => {
    try {
      await logoutMutation().unwrap();
      return { success: true };
    } catch (error) {
      // Даже при ошибке считаем выход успешным.
      return { success: true };
    }
  };

  const clearAuthError = () => {
    dispatch(clearError());
  };

  return {
    ...authState,
    isLoading,
    login,
    logout,
    clearError: clearAuthError,
  };
};

/**
 * Хук инициализации аутентификации при загрузке приложения.
 * Используется в Authmiddleware / Login mount — делает один initializeAuth fetch.
 */
export const useAuthInitialization = () => {
  const isInitialized = useSelector(selectIsInitialized);
  const [initializeAuth] = useInitializeAuthMutation();

  useEffect(() => {
    if (!isInitialized) {
      initializeAuth();
    }
  }, [initializeAuth, isInitialized]);

  return { isInitialized };
};
